import React, { useEffect, useRef, useState, useMemo } from "react";
import Chart from "chart.js/auto";
import "./UserCharts.css";
import "chartjs-adapter-date-fns";

function PersonalityUserCharts({ user }) {
  const radarRef = useRef(null);
  const radarCanvasRef = useRef(null);
  const scatterRef = useRef(null);
  const scatterCanvasRef = useRef(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const categories = useMemo(
    () => [
      "Extraversion",
      "Agreeableness",
      "Conscientiousness",
      "Emotional Stability",
      "Intellect/Imagination",
    ],
    []
  );

  const parseDate = (d) => {
    if (!d) return null;
    if (typeof d === "string") return new Date(d);
    if (d && d.$date) return new Date(d.$date);
    try {
      return new Date(d);
    } catch {
      return null;
    }
  };

  const toLocalDateOnly = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  };

  // Radar chart
  useEffect(() => {
    if (!user || !radarCanvasRef.current || !user.scores?.length) return;

    const latestObj = user.scores[user.scores.length - 1];
    const values = categories.map((cat) => {
      const scoreVal = latestObj?.score?.[cat];
      const maxVal = latestObj?.maxScore?.[cat];
      return scoreVal != null && maxVal > 0 ? (scoreVal / maxVal) * 100 : 0;
    });

    const config = {
      type: "radar",
      data: {
        labels: categories,
        datasets: [
          {
            label: `${user.username || user.email}'s Latest Scores`,
            data: values,
            backgroundColor: "rgba(75, 192, 192, 0.2)",
            borderColor: "rgb(75, 192, 192)",
            pointBackgroundColor: "rgb(75, 192, 192)",
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: { legend: { position: "top" } },
        scales: {
          r: { suggestedMin: 0, suggestedMax: 100, ticks: { stepSize: 20 } },
        },
      },
    };

    if (radarRef.current) radarRef.current.destroy();
    radarRef.current = new Chart(radarCanvasRef.current, config);

    return () => radarRef.current?.destroy();
  }, [user, categories]);

  // Scatter chart
  useEffect(() => {
    if (!user || !user.scores?.length || !scatterCanvasRef.current) return;

    const startDateObj = startDate ? toLocalDateOnly(startDate) : null;
    const endDateObj = endDate ? toLocalDateOnly(endDate) : null;

    const perCategoryData = {};
    categories.forEach((cat) => (perCategoryData[cat] = []));

    user.scores.forEach((entry) => {
      const date = parseDate(entry.date);
      if (!date || isNaN(date)) return;
      const dateOnly = toLocalDateOnly(date);

      if (startDateObj && dateOnly < startDateObj) return;
      if (endDateObj && dateOnly > endDateObj) return;

      categories.forEach((cat) => {
        const s = entry.score?.[cat];
        const m = entry.maxScore?.[cat];
        if (s != null && m > 0) {
          perCategoryData[cat].push({ x: date, y: (s / m) * 100 });
        }
      });
    });

    const config = {
      type: "scatter",
      data: {
        datasets: categories.map((cat, i) => ({
          label: cat,
          data: perCategoryData[cat],
          backgroundColor: `hsl(${(i * 72) % 360}, 70%, 60%)`,
          borderColor: `hsl(${(i * 72) % 360}, 70%, 40%)`,
        })),
      },
      options: {
        responsive: true,
        plugins: {
          title: {
            display: true,
            text: `${user.username || user.email} – Score History`,
          },
        },
        scales: {
          x: {
            type: "time",
            time: { unit: "day" },
            title: { display: true, text: "Date" },
          },
          y: {
            title: { display: true, text: "Score (%)" },
            suggestedMin: 0,
            suggestedMax: 100,
          },
        },
      },
    };

    if (scatterRef.current) scatterRef.current.destroy();
    scatterRef.current = new Chart(scatterCanvasRef.current, config);

    return () => scatterRef.current?.destroy();
  }, [user, startDate, endDate, categories]);

  if (!user) return <div>No user selected</div>;
  if (!user.scores || user.scores.length === 0) {
    return (
      <div>
        {user.username || user.email} has not taken the personality quiz yet.
      </div>
    );
  }

  return (
    <div
      className="user-charts"
      style={{ display: "flex", flexDirection: "column", gap: "24px" }}
    >
      {/* Radar */}
      <div>
        <h3>{user.username || user.email} – Latest Personality Scores</h3>
        <canvas ref={radarCanvasRef}></canvas>
        <div style={{ marginTop: 8 }}>
          <button
            onClick={() => {
              if (radarRef.current) {
                const link = document.createElement("a");
                link.href = radarRef.current.toBase64Image();
                link.download = `${user.username || user.email}_radar.png`;
                link.click();
              }
            }}
          >
            Download PNG
          </button>{" "}
          <button
            onClick={() => {
              if (!radarRef.current) return;
              const chart = radarRef.current;
              const csvRows = [["Trait", ...chart.data.labels]];
              chart.data.datasets.forEach((ds) =>
                csvRows.push([
                  ds.label,
                  ...ds.data.map((v) => Number(v).toFixed(2)),
                ])
              );
              const blob = new Blob(
                [csvRows.map((r) => r.join(",")).join("\n")],
                { type: "text/csv" }
              );
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = `${user.username || user.email}_radar.csv`;
              link.click();
            }}
          >
            Export Radar Data to CSV
          </button>
        </div>
      </div>

      {/* Date pickers */}
      <div
        style={{
          margin: "16px 0",
          display: "flex",
          justifyContent: "center", // Centers the content horizontally
          alignItems: "center", // Centers the content vertically
          width: "100%", // Ensures the div takes the full width of its container
          textAlign: "center", // Aligns text within the div
          gap: "16px", // Adds spacing between the labels
        }}
      >
        <label style={{ marginRight: "8px" }}>
          Start Date:
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              const newStart = e.target.value;
              setStartDate(newStart);

              // Reset end date if it's before new start date
              if (endDate && new Date(endDate) < new Date(newStart)) {
                setEndDate("");
              }
            }}
            style={{ marginLeft: "4px" }}
          />
        </label>
        <label>
          End Date:
          <input
            type="date"
            value={endDate}
            min={startDate || undefined} // Prevents choosing an endDate before startDate
            onChange={(e) => setEndDate(e.target.value)}
            style={{ marginLeft: "4px" }}
          />
        </label>
      </div>

      {/* Validation message */}
      {startDate && endDate && new Date(endDate) < new Date(startDate) && (
        <div style={{ color: "red", marginTop: "8px" }}>
          End date cannot be before start date.
        </div>
      )}

      {/* Scatter */}
      <div>
        <h3>{user.username || user.email} – Score History</h3>
        <canvas ref={scatterCanvasRef} width={600} height={400}></canvas>
        <div style={{ marginTop: 8 }}>
          <button
            onClick={() => {
              if (scatterRef.current) {
                const link = document.createElement("a");
                link.href = scatterRef.current.toBase64Image();
                link.download = `${user.username || user.email}_scatter.png`;
                link.click();
              }
            }}
          >
            Download PNG
          </button>{" "}
          <button
            onClick={() => {
              if (!scatterRef.current) return;
              const chart = scatterRef.current;
              const csvRows = [["Category", "Date", "Score (%)"]];
              chart.data.datasets.forEach((ds) =>
                ds.data.forEach((pt) => {
                  const date =
                    pt.x instanceof Date
                      ? pt.x.toISOString()
                      : new Date(pt.x).toISOString();
                  csvRows.push([ds.label, date, Number(pt.y).toFixed(2)]);
                })
              );
              const blob = new Blob(
                [csvRows.map((r) => r.join(",")).join("\n")],
                { type: "text/csv" }
              );
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = `${user.username || user.email}_scatter.csv`;
              link.click();
            }}
          >
            Export Scatter Data to CSV
          </button>
        </div>
      </div>
    </div>
  );
}

export default PersonalityUserCharts;
